// ============================================================================
// ERROR HANDLING UTILITIES
// ============================================================================
// Centralized error handling utilities for API errors and user notifications

import type { AxiosError } from 'axios';

/**
 * Error severity levels
 */
export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

/**
 * Error types for categorization
 */
export enum ErrorType {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  AUTHORIZATION = 'authorization',
  VALIDATION = 'validation',
  NOT_FOUND = 'not_found',
  SERVER = 'server',
  TIMEOUT = 'timeout',
  UNKNOWN = 'unknown',
}

/**
 * Structured error object
 */
export interface AppError {
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  userMessage: string;
  details?: any;
  code?: string;
  statusCode?: number;
  timestamp: string;
}

/**
 * Notification callback type
 */
export type NotificationHandler = (
  message: string,
  type: ErrorSeverity,
  details?: any
) => void;

/**
 * Global notification handler (can be set by the application)
 */
let globalNotificationHandler: NotificationHandler | null = null;

/**
 * Register a global notification handler
 */
export const registerNotificationHandler = (
  handler: NotificationHandler
): void => {
  globalNotificationHandler = handler;
};

/**
 * Show notification using the registered handler
 */
const showNotification = (
  message: string,
  severity: ErrorSeverity,
  details?: any
): void => {
  if (globalNotificationHandler) {
    globalNotificationHandler(message, severity, details);
  } else {
    // Fallback to console if no handler is registered
    console.log(`[${severity.toUpperCase()}] ${message}`, details || '');
  }
};

/**
 * Map HTTP status codes to error types
 */
const getErrorTypeFromStatus = (statusCode?: number): ErrorType => {
  if (!statusCode) return ErrorType.UNKNOWN;

  if (statusCode === 401) return ErrorType.AUTHENTICATION;
  if (statusCode === 403) return ErrorType.AUTHORIZATION;
  if (statusCode === 404) return ErrorType.NOT_FOUND;
  if (statusCode >= 400 && statusCode < 500) return ErrorType.VALIDATION;
  if (statusCode >= 500) return ErrorType.SERVER;

  return ErrorType.UNKNOWN;
};

/**
 * Get error severity based on error type
 */
const getErrorSeverity = (type: ErrorType): ErrorSeverity => {
  switch (type) {
    case ErrorType.AUTHENTICATION:
    case ErrorType.AUTHORIZATION:
      return ErrorSeverity.WARNING;
    case ErrorType.VALIDATION:
    case ErrorType.NOT_FOUND:
      return ErrorSeverity.INFO;
    case ErrorType.SERVER:
      return ErrorSeverity.CRITICAL;
    case ErrorType.NETWORK:
    case ErrorType.TIMEOUT:
      return ErrorSeverity.ERROR;
    default:
      return ErrorSeverity.ERROR;
  }
};

/**
 * Get user-friendly error messages
 */
const getUserFriendlyMessage = (
  type: ErrorType,
  statusCode?: number,
  originalMessage?: string
): string => {
  switch (type) {
    case ErrorType.NETWORK:
      return 'Unable to connect to the server. Please check your internet connection.';
    case ErrorType.AUTHENTICATION:
      return 'Your session has expired. Please log in again.';
    case ErrorType.AUTHORIZATION:
      return 'You do not have permission to perform this action.';
    case ErrorType.NOT_FOUND:
      return 'The requested resource was not found.';
    case ErrorType.VALIDATION:
      return originalMessage || 'Please check your input and try again.';
    case ErrorType.TIMEOUT:
      return 'The request took too long to complete. Please try again.';
    case ErrorType.SERVER:
      return statusCode === 503
        ? 'The service is temporarily unavailable. Please try again later.'
        : 'An error occurred on the server. Please try again later.';
    default:
      return originalMessage || 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Extract error message from various error formats
 */
const extractErrorMessage = (error: any): string => {
  // Check for API error format
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }

  // Check for simple message format
  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  // Check for error string
  if (error.message) {
    return error.message;
  }

  return 'An unexpected error occurred';
};

/**
 * Extract error details from response
 */
const extractErrorDetails = (error: any): any => {
  if (error.response?.data?.error?.details) {
    return error.response.data.error.details;
  }

  if (error.response?.data?.errors) {
    return error.response.data.errors;
  }

  return undefined;
};

/**
 * Main error handling function
 * Converts various error types into structured AppError and shows notifications
 */
export const handleApiError = (
  error: any,
  options?: {
    showNotification?: boolean;
    customMessage?: string;
    logError?: boolean;
    rethrow?: boolean;
  }
): AppError => {
  const {
    showNotification: shouldShowNotification = true,
    customMessage,
    logError = true,
    rethrow = false,
  } = options || {};

  let appError: AppError;

  // Handle Axios errors
  if (error.isAxiosError) {
    const axiosError = error as AxiosError;
    const statusCode = axiosError.response?.status;
    const errorCode = (axiosError.response?.data as any)?.error?.code;

    // Determine error type
    let errorType: ErrorType;
    if (!axiosError.response) {
      errorType = axiosError.code === 'ECONNABORTED'
        ? ErrorType.TIMEOUT
        : ErrorType.NETWORK;
    } else {
      errorType = getErrorTypeFromStatus(statusCode);
    }

    const originalMessage = extractErrorMessage(axiosError);
    const errorDetails = extractErrorDetails(axiosError);

    appError = {
      type: errorType,
      severity: getErrorSeverity(errorType),
      message: originalMessage,
      userMessage: customMessage || getUserFriendlyMessage(errorType, statusCode, originalMessage),
      details: errorDetails,
      code: errorCode,
      statusCode,
      timestamp: new Date().toISOString(),
    };
  }
  // Handle standard Error objects
  else if (error instanceof Error) {
    appError = {
      type: ErrorType.UNKNOWN,
      severity: ErrorSeverity.ERROR,
      message: error.message,
      userMessage: customMessage || getUserFriendlyMessage(ErrorType.UNKNOWN, undefined, error.message),
      timestamp: new Date().toISOString(),
    };
  }
  // Handle unknown errors
  else {
    appError = {
      type: ErrorType.UNKNOWN,
      severity: ErrorSeverity.ERROR,
      message: String(error),
      userMessage: customMessage || 'An unexpected error occurred. Please try again.',
      timestamp: new Date().toISOString(),
    };
  }

  // Log error if requested
  if (logError) {
    console.error('[Error Handler]', {
      type: appError.type,
      message: appError.message,
      userMessage: appError.userMessage,
      code: appError.code,
      statusCode: appError.statusCode,
      details: appError.details,
      timestamp: appError.timestamp,
    });
  }

  // Show notification if requested
  if (shouldShowNotification) {
    showNotification(appError.userMessage, appError.severity, appError.details);
  }

  // Rethrow if requested
  if (rethrow) {
    throw appError;
  }

  return appError;
};

/**
 * Handle validation errors specifically
 */
export const handleValidationError = (
  errors: Record<string, string[]> | string[],
  options?: {
    showNotification?: boolean;
    customMessage?: string;
  }
): AppError => {
  const { showNotification: shouldShowNotification = true, customMessage } = options || {};

  let errorMessage: string;
  let errorDetails: any;

  if (Array.isArray(errors)) {
    errorMessage = errors.join(', ');
    errorDetails = { errors };
  } else {
    const allErrors = Object.entries(errors)
      .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
      .join('; ');
    errorMessage = allErrors;
    errorDetails = errors;
  }

  const appError: AppError = {
    type: ErrorType.VALIDATION,
    severity: ErrorSeverity.INFO,
    message: errorMessage,
    userMessage: customMessage || 'Please correct the errors and try again.',
    details: errorDetails,
    timestamp: new Date().toISOString(),
  };

  if (shouldShowNotification) {
    showNotification(appError.userMessage, appError.severity, appError.details);
  }

  return appError;
};

/**
 * Handle success notifications
 */
export const showSuccessNotification = (message: string): void => {
  showNotification(message, ErrorSeverity.INFO);
};

/**
 * Handle warning notifications
 */
export const showWarningNotification = (message: string, details?: any): void => {
  showNotification(message, ErrorSeverity.WARNING, details);
};

/**
 * Handle info notifications
 */
export const showInfoNotification = (message: string, details?: any): void => {
  showNotification(message, ErrorSeverity.INFO, details);
};

/**
 * Check if error is a specific type
 */
export const isErrorType = (error: AppError, type: ErrorType): boolean => {
  return error.type === type;
};

/**
 * Check if error is authentication related
 */
export const isAuthError = (error: AppError): boolean => {
  return error.type === ErrorType.AUTHENTICATION;
};

/**
 * Check if error is authorization related
 */
export const isAuthorizationError = (error: AppError): boolean => {
  return error.type === ErrorType.AUTHORIZATION;
};

/**
 * Check if error is validation related
 */
export const isValidationError = (error: AppError): boolean => {
  return error.type === ErrorType.VALIDATION;
};

/**
 * Check if error is network related
 */
export const isNetworkError = (error: AppError): boolean => {
  return error.type === ErrorType.NETWORK || error.type === ErrorType.TIMEOUT;
};

/**
 * Format error for display in UI
 */
export const formatErrorForDisplay = (error: AppError): string => {
  let message = error.userMessage;

  if (error.details && typeof error.details === 'object') {
    const detailsStr = Object.entries(error.details)
      .map(([key, value]) => `${key}: ${value}`)
      .join('\n');
    message += `\n\n${detailsStr}`;
  }

  return message;
};

/**
 * Create a retry function for failed requests
 */
export const createRetryHandler = <T extends (...args: any[]) => Promise<any>>(
  fn: T,
  maxRetries: number = 3,
  delayMs: number = 1000
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
  return async (...args: Parameters<T>): Promise<ReturnType<T>> => {
    let lastError: any;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn(...args);
      } catch (error) {
        lastError = error;
        const appError = handleApiError(error, {
          showNotification: false,
          logError: true,
        });

        // Don't retry on certain error types
        if (
          isAuthError(appError) ||
          isAuthorizationError(appError) ||
          isValidationError(appError)
        ) {
          throw error;
        }

        // If this was the last attempt, throw the error
        if (attempt === maxRetries) {
          throw error;
        }

        // Wait before retrying
        await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
      }
    }

    throw lastError;
  };
};

/**
 * Export all error handling utilities
 */
export default {
  handleApiError,
  handleValidationError,
  showSuccessNotification,
  showWarningNotification,
  showInfoNotification,
  registerNotificationHandler,
  isErrorType,
  isAuthError,
  isAuthorizationError,
  isValidationError,
  isNetworkError,
  formatErrorForDisplay,
  createRetryHandler,
  ErrorSeverity,
  ErrorType,
};

/**
 * The machine-readable error code of an API failure, or undefined.
 *
 * The platform envelope is code-first: `errors[0]` is an UPPER_SNAKE_CASE code
 * (`VALIDATION_FAILED`, `DUPLICATE`, `CONFLICT`, `SESSION_EXPIRED`, …) and the rest of
 * `errors` is human detail. Branch on this, never on `message` text. The nested
 * `error.code` shape is kept for back ends that use it.
 */
export const apiErrorCode = (error: any): string | undefined => {
  const data = error?.response?.data;
  const first = Array.isArray(data?.errors) ? data.errors[0] : undefined;
  if (typeof first === 'string' && /^[A-Z][A-Z0-9_]+$/.test(first)) {
    return first;
  }
  const nested = data?.error?.code;
  return typeof nested === 'string' && nested ? nested : undefined;
};
